import React from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import TablePagination from '@mui/material/TablePagination';
import FilterListIcon from '@mui/icons-material/FilterList';
import TableContainer from '@mui/material/TableContainer';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import Checkbox from '@mui/material/Checkbox';
import TableRow from '@mui/material/TableRow';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import Link from '@mui/material/Link';
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { LoadButtons } from '../components/LoadButtons';
import { LoadButtonHeaderTable } from '../../../components/sharedViews/loadActionsHeader/LoadButtonHeaderTable';
import { FilterTable } from '../../../components/others/FilterTable';
import { formatNumber } from '../../../utils/invoice';
import identificationTypes from "../../../data/identificationTypes.json";
import { DialogSelectCompany } from '../../../components/dialogs/DialogSelectCompany';
import { useManageSharedViews } from '../hooks/useManageSharedViews';
import { TruncateText } from '../../../components/others/TruncateText';
import { getNestedValue } from '../../../utils/objectHandlers';

const EMPTY = () => {
    const { t } = useTranslation();
    return <Typography textAlign="center" py={6}>{t('messages.noRecords')}</Typography>
};

const LOADING = () => (
    <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress />
    </Box>
);

export const LayoutSharedViews = ({ config = null }) => {
    const {
        isMobileScreen,
        DocManager,
        configuration,
        buttons,
        columns,
        id_user_action,
        companies,
        openDialogCompany,
        expandedId,
        anchorEl,
        //REDUX DATA
        listAllDocs,
        isLoading,
        rowsChecked,
        filters,
        pagination,
        counter,
        //REDUX DATA
        // custom functions
        t,
        setAnchorEl,
        handleConfirmAction,
        handleChangePage,
        handleChangeRowsPerPage,
        Search,
        ResetSearch,
        RefreshTable,
        handleCheckAll,
        handleCheck,
        handleLoadMore,
        isCheck,
        Cancel
        // custom functions
    } = useManageSharedViews(config);

    return (
        <Box sx={{ width: '100%' }} className='custom-hard-table-zd-nix'>
            <Box sx={{ mb: 2, pt: 2, pl: 2, pr: 2 }}>
                <Box className="box-container-actions-header"
                    sx={{
                        flexDirection: isMobileScreen ? 'column' : 'row',
                        alignItems: isMobileScreen ? 'flex-start' : 'center'
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Breadcrumbs aria-label='breadcrumb' separator='›'>
                            <Link underline='hover' color='text.main' sx={{ cursor: 'pointer' }}>
                                <Typography sx={{ fontWeight: 'bold' }}>
                                    {configuration?.title || 'Consultar documentos'}
                                </Typography>
                            </Link>
                        </Breadcrumbs>
                    </Box>

                    <Box className="box-actions-header"
                        sx={{
                            flexWrap: isMobileScreen ? 'wrap' : 'nowrap',
                            width: isMobileScreen ? '100%' : 'auto'
                        }}
                    >
                        {isMobileScreen && (
                            <>
                                <IconButton color="primary"
                                    onClick={(e) => setAnchorEl(e.currentTarget)}
                                >
                                    <FilterListIcon />
                                </IconButton>
                                <Menu PaperProps={{ sx: { minWidth: 220, p: 1 } }}
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={() => setAnchorEl(null)}
                                >
                                    <Typography variant="subtitle2" className="typographyFilters">
                                        {t('filters')}
                                    </Typography>

                                    {columns.filter(col => col.filterable).map((col, idx) => (
                                        <MenuItem key={idx} className="menuItemLanguage">
                                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                                                {col.label}
                                            </Typography>
                                            <FilterTable colum={col} Search={Search} ResetSearch={ResetSearch} filter={filters.hasOwnProperty(col.id)} />
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </>
                        )}

                        <LoadButtonHeaderTable
                            buttons={buttons}
                            id_user_action={id_user_action}
                            DocManager={DocManager}
                            columns={columns}
                            handleChange={RefreshTable}
                        />
                    </Box>
                </Box>


                {/* MOBILE VIEW */}
                {isMobileScreen ? (
                    <Box className="mobileListContainer">
                        {isLoading ? (<LOADING />) : listAllDocs.length === 0 ? (<EMPTY />) : (
                            listAllDocs.map((item) => (
                                <Paper key={item.id} className="paper-item"
                                    isChecked={isCheck(item.id)}
                                    sx={(theme) => ({
                                        backgroundColor: isCheck(item.id)
                                            ? `${theme.palette.primary.main}14`
                                            : theme.palette.background.paper,
                                        border: isCheck(item.id)
                                            ? `1px solid ${theme.palette.primary.main}44`
                                            : "1px solid transparent"
                                    })}
                                >
                                    <Box className="item-flex-container">
                                        <Box className="checkboxWrapper">
                                            <Checkbox checked={isCheck(item.id)} className="customCheckbox"
                                                onChange={() => handleCheck(item.id)}
                                                icon={<RadioButtonUncheckedIcon fontSize="medium" />}
                                                checkedIcon={<CheckCircleIcon fontSize="medium" />}
                                            />
                                        </Box>

                                        <Box sx={{ minWidth: 0 }}>
                                            {expandedId === item.id ?
                                                (columns.filter(col => col.visible).map((col, idx) =>
                                                (<Box key={idx} className="expanded-field">
                                                    <Typography variant="caption" className="field-label">
                                                        {col.label}:
                                                    </Typography>
                                                    <Typography variant="body2" className="field-value">
                                                        {col.type === 'money'
                                                            ? formatNumber(item[col.id])
                                                            : col.id === 'type_identification'
                                                                ? identificationTypes.find(
                                                                    (type) => type.value === item[col.id]
                                                                )?.label || 'Desconocido'
                                                                : item[col.id] || '-'}
                                                    </Typography>
                                                </Box>))
                                                ) :
                                                (columns.filter(col => col.visible && (col.filterable || col.showMobile)).map((col, idx) => (
                                                    <Typography key={idx} variant="body2" className="text-ellipsis-dots">
                                                        {col.type === 'money'
                                                            ? formatNumber(item[col.id])
                                                            : col.id === 'type_identification'
                                                                ? identificationTypes.find(
                                                                    (type) => type.value === item[col.id]
                                                                )?.label || 'Desconocido'
                                                                : item[col.id] || '-'}
                                                    </Typography>
                                                )))
                                            }
                                        </Box>
                                    </Box>
                                    <Box className="loadButtonsWrapper">
                                        <LoadButtons data={item} handleChange={RefreshTable} DocManager={DocManager} isMobileScreen={isMobileScreen} />
                                    </Box>
                                </Paper>
                            ))
                        )}

                        {(!isLoading && listAllDocs.length < counter) && (
                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Typography variant="button" className="loadMoreButton" onClick={handleLoadMore}>
                                    {t('loadMore')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ width: '100%' }}>
                        <Paper
                            sx={{
                                width: '100%',
                                mb: 2,
                                boxShadow: (theme) => theme.palette.dark === 'dark' ? '0px 0px 8px 0px rgba(0,0,0,0.75)' : '0px 0px 8px 0px rgba(0,0,0,0.25)',
                                WebkitBoxShadow: (theme) => theme.palette.dark === 'dark' ? '0px 0px 8px 0px rgba(0,0,0,0.75)' : '0px 0px 8px 0px rgba(0,0,0,0.25)',
                                bgcolor: (theme) => theme.palette.paper.background
                            }}
                            elevation={3}
                        >
                            <TableContainer
                                style={{ height: '72vh', overflow: 'auto', borderBottom: 'solid 1px' }}
                            >
                                <Table
                                    aria-labelledby='tableTitle'
                                    size='small'
                                    stickyHeader
                                >
                                    <TableHead>
                                        <TableRow sx={{ px: 2 }}>
                                            <TableCell
                                                sx={{
                                                    minWidth: 40,
                                                    position: 'sticky',
                                                    zIndex: 1000,
                                                    left: 0
                                                }}
                                                id='TcellCheck'
                                            >
                                                <Checkbox
                                                    checked={listAllDocs.length > 0 && rowsChecked.length === listAllDocs.length}
                                                    onChange={handleCheckAll}
                                                    icon={<RadioButtonUncheckedIcon fontSize="medium" />}
                                                    checkedIcon={<CheckCircleIcon fontSize="medium" />}
                                                    color="primary"
                                                    sx={{
                                                        "&.Mui-checked": {
                                                            color: 'black',
                                                            transform: "scale(1.1)",
                                                            transition: "transform 0.2s ease",
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell
                                                padding='normal'
                                                align='center'
                                                sx={{ minWidth: 120, position: 'sticky', left: 52, zIndex: 1000, top: '-1px' }}
                                            >
                                                <Typography sx={{ fontWeight: 'bold' }} component='span'>
                                                    {t('actions')}
                                                </Typography>
                                            </TableCell>
                                            {
                                                columns.map((col, index) => (
                                                    col.visible &&
                                                    <TableCell
                                                        padding='normal'
                                                        align='center'
                                                        key={`tc-${index}`}
                                                        sx={{
                                                            width: col?.width ? `${col?.width}px` : "auto",
                                                            minWidth: col?.width ? `${col?.width}px` : "auto",
                                                            maxWidth: col?.width ? `${col?.width}px` : "auto",
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <Typography sx={{ fontWeight: 'bold', marginRight: '8px' }} component='span'>
                                                                {col.label}
                                                            </Typography>
                                                            {col.filterable && (
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <FilterTable colum={col} Search={Search} ResetSearch={ResetSearch} filter={filters.hasOwnProperty(col.id)} />
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                ))
                                            }
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {isLoading ? (
                                            <TableRow sx={{ height: 200, py: 2 }}>
                                                <TableCell colSpan={columns.length + 2} align='center'>
                                                    <LOADING />
                                                </TableCell>
                                            </TableRow>
                                        ) : !isLoading && !listAllDocs.length > 0 ? (
                                            <TableRow sx={{ height: 200, py: 2 }}>
                                                <TableCell colSpan={columns.length + 2} align='center'>
                                                    <EMPTY />
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            listAllDocs.map((item) => (
                                                <TableRow tabIndex={-1} key={item.id}>
                                                    <TableCell
                                                        style={{ position: 'sticky', left: 0, backgroundColor: 'white' }}
                                                    >
                                                        <Checkbox
                                                            key={item.id}
                                                            checked={isCheck(item.id)}
                                                            onChange={() => handleCheck(item.id)}
                                                            icon={<RadioButtonUncheckedIcon fontSize="medium" />}
                                                            checkedIcon={<CheckCircleIcon fontSize="medium" />}
                                                            color="primary"
                                                            className="customCheckbox"
                                                            sx={{ justifyContent: "center" }}
                                                        />
                                                    </TableCell>
                                                    <TableCell
                                                        padding='normal'
                                                        align='center'
                                                        sx={{ position: 'sticky', left: 52, backgroundColor: 'white' }}
                                                    >
                                                        <Typography>
                                                            <LoadButtons data={item} handleChange={RefreshTable} DocManager={DocManager} isMobileScreen={isMobileScreen} />
                                                        </Typography>
                                                    </TableCell>
                                                    {
                                                        columns.filter(col => col.visible)
                                                            .map((col, index) => {
                                                                let content = getNestedValue(item, col.id);
                                                                if (col.type === 'money') {
                                                                    content = formatNumber(item[col.id]);
                                                                } else if (col.id === 'type_identification') {
                                                                    content = identificationTypes.find(type => type.value === item[col.id])?.label || 'Desconocido';
                                                                } else {
                                                                    content = item[col.id];
                                                                }

                                                                return (
                                                                    <TableCell key={`tc-${index}`} padding='normal' align={col?.align || 'center'}>
                                                                        {col?.truncate ?
                                                                            <TruncateText
                                                                                text={content || ''}
                                                                                limit={col.truncate}
                                                                            />
                                                                            :
                                                                            <Typography>{content}</Typography>
                                                                        }
                                                                    </TableCell>
                                                                );
                                                            })
                                                    }
                                                </TableRow>
                                            ))
                                        )
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                sx={{ display: 'table-row', verticalAlign: 'bottom' }}
                                rowsPerPageOptions={[25, 50, 100]}
                                component='Box'
                                count={counter}
                                rowsPerPage={pagination.pageSize}
                                page={pagination.pageNumber - 1}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Paper>
                    </Box>
                )}
            </Box>

            {
                openDialogCompany && (
                    <DialogSelectCompany
                        openDialogConfirm={openDialogCompany}
                        handleCancelConfirm={Cancel}
                        handleConfirmAction={handleConfirmAction}
                        data={companies}
                    />
                )
            }
        </Box >
    )
}